import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Bosishga javob — `active:scale`.
 *
 * Tugma bosilganda 2% ga kichrayadi va qo'yib yuborilganda joyiga qaytadi. Bu bezak emas:
 * paneldagi ko'p amal (efirda bosqich e'lon qilish, chekni rad etish) sekundlarga cho'ziladigan
 * so'rov yuboradi, va bosish RO'YXATGA OLINGANIni bir zumda bildirmaslik operatorni ikkinchi
 * marta bosishga undaydi. Yuklanish aylanasi keyin keladi; bu esa darhol.
 *
 * Havolaga aylangan tugma (`asChild`) ham shu bilan javob beradi — foydalanuvchi uchun ular
 * bir xil ko'rinadigan narsa.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-(--dur-control) ease-(--ease-out-soft) active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 [&_svg]:transition-transform outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 hover:shadow-[0_2px_12px_-2px_var(--primary)]',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 hover:shadow-[0_2px_12px_-2px_var(--destructive)] focus-visible:ring-destructive/20',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:border-ring/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Ish ketayotganini bildiradi va tugmani o'chiradi.
   *
   * Ikkinchi bosish bu panelda arzon xato emas: `POST /reveal` ikki marta ketsa efirda bosqich
   * ikki marta e'lon qilinadi. Shuning uchun "loading" holati tugmaning o'z qismi.
   */
  loading?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, className }));

  // `asChild` — bola BITTA element bo'lishi shart (Radix `Slot` shartidir), ya'ni bu tarmoqqa
  // yuklanish aylanasini ham, `type`/`disabled` ni ham qo'shib bo'lmaydi: bola `<a>` bo'lishi
  // mumkin, va `disabled` havolada ma'nosiz atribut.
  if (asChild) {
    return (
      <Slot data-slot="button" className={classes} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      data-slot="button"
      className={classes}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2Icon className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export { Button };
